import {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { FOOTER_FRAGMENT_URL, HEADER_FRAGMENT_URL } from '@config/constants';
import { fetchHtml } from '@utils';
import {
  getHeaderFooterConfig,
  getInlineStyleContents,
  getStylesheetUrls,
  injectInlineStyles,
  loadStylesheets,
  resolveProxiedFetchUrl,
  triggerAEMHeaderCartIcon,
} from './utils';

type AEMResourceState = {
  header: string;
  footer: string;
  headerScript: HTMLScriptElement[];
  headerLoaded: boolean;
  footerLoaded: boolean;
  headerCssReady: boolean;
  footerCssReady: boolean;
};

const initialState: AEMResourceState = {
  header: '',
  footer: '',
  headerScript: [],
  headerLoaded: false,
  footerLoaded: false,
  headerCssReady: false,
  footerCssReady: false,
};

const AEMResourceContext = createContext<AEMResourceState>(initialState);

type FetchedResource = {
  html: string;
  sourceUrl: string;
};

const fetchResourceWithFallback = async (
  configuredUrl: string,
  fallbackUrl: string,
  resourceName: 'header' | 'footer',
): Promise<FetchedResource> => {
  const resolvedConfiguredUrl = resolveProxiedFetchUrl(configuredUrl);
  const configuredResource = await fetchHtml(resolvedConfiguredUrl);

  if (configuredResource.trim()) {
    return {
      html: configuredResource,
      sourceUrl: configuredUrl,
    };
  }

  const resolvedFallbackUrl = resolveProxiedFetchUrl(fallbackUrl);

  if (resolvedConfiguredUrl === resolvedFallbackUrl) {
    return {
      html: '',
      sourceUrl: fallbackUrl,
    };
  }

  // eslint-disable-next-line no-console
  console.warn(
    `Unable to load ${resourceName} from ${configuredUrl}. Using fallback URL.`,
  );

  const fallbackResource = await fetchHtml(resolvedFallbackUrl);

  return {
    html: fallbackResource,
    sourceUrl: fallbackUrl,
  };
};

export const AEMResourceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<AEMResourceState>(initialState);

  useEffect(() => {
    let cancelled = false;

    const fetchHeaderData = async (
      headerUrl: string,
      headerFooterCSS?: string,
    ) => {
      try {
        const headerResource = await fetchResourceWithFallback(
          headerUrl,
          HEADER_FRAGMENT_URL,
          'header',
        );

        if (cancelled) return;

        const headerDoc = new DOMParser().parseFromString(
          headerResource.html,
          'text/html',
        );

        const headerScript = Array.from(
          headerDoc.querySelectorAll('script'),
        );

        const stylesheetUrls = getStylesheetUrls(
          headerDoc,
          headerResource.sourceUrl,
        );

        if (headerFooterCSS) {
          stylesheetUrls.push(headerFooterCSS);
        }

        injectInlineStyles(getInlineStyleContents(headerDoc));

        try {
          await loadStylesheets(stylesheetUrls);
        } catch (stylesheetError) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to load header stylesheets:',
            stylesheetError,
          );
        }

        if (cancelled) return;

        setState((currentState) => ({
          ...currentState,
          header: headerDoc.body.innerHTML,
          headerScript,
          headerLoaded: true,
          headerCssReady: true,
        }));
      } catch (error) {
        if (cancelled) return;

        // eslint-disable-next-line no-console
        console.error('Header fetch error:', error);

        setState((currentState) => ({
          ...currentState,
          headerLoaded: true,
          headerCssReady: true,
        }));
      }
    };

    const fetchFooterData = async (footerUrl: string) => {
      try {
        const footerResource = await fetchResourceWithFallback(
          footerUrl,
          FOOTER_FRAGMENT_URL,
          'footer',
        );

        if (cancelled) return;

        const footerDoc = new DOMParser().parseFromString(
          footerResource.html,
          'text/html',
        );

        injectInlineStyles(getInlineStyleContents(footerDoc));

        try {
          await loadStylesheets(
            getStylesheetUrls(
              footerDoc,
              footerResource.sourceUrl,
            ),
          );
        } catch (stylesheetError) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to load footer stylesheets:',
            stylesheetError,
          );
        }

        if (cancelled) return;

        setState((currentState) => ({
          ...currentState,
          footer: footerDoc.body.innerHTML,
          footerLoaded: true,
          footerCssReady: true,
        }));
      } catch (error) {
        if (cancelled) return;

        // eslint-disable-next-line no-console
        console.error('Footer fetch error:', error);

        setState((currentState) => ({
          ...currentState,
          footerLoaded: true,
          footerCssReady: true,
        }));
      }
    };

    const initHeaderFooter = async () => {
      try {
        const {
          headerUrl,
          footerUrl,
          headerFooterCSS,
        } = await getHeaderFooterConfig();

        if (cancelled) return;

        // Load header and footer independently so neither blocks the other
        fetchHeaderData(headerUrl, headerFooterCSS).catch(() => undefined);
        fetchFooterData(footerUrl).catch(() => undefined);
      } catch (error) {
        if (cancelled) return;

        // eslint-disable-next-line no-console
        console.error('Header/footer config error:', error);

        setState((currentState) => ({
          ...currentState,
          headerLoaded: true,
          footerLoaded: true,
          headerCssReady: true,
          footerCssReady: true,
        }));
      }
    };

    initHeaderFooter();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state.headerLoaded && state.header) {
      triggerAEMHeaderCartIcon();
    }
  }, [state.header, state.headerLoaded]);

  const payload = useMemo(() => state, [state]);

  return (
    <AEMResourceContext.Provider value={payload}>
      {children}
    </AEMResourceContext.Provider>
  );
};

export const useAEMResource = (): AEMResourceState => {
  const context = useContext(AEMResourceContext);

  if (!context) {
    throw new Error(
      'useAEMResource must be used within an AEMResourceProvider',
    );
  }

  return context;
};
